// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, {
    CheckboxPresentationProps,
    StyleProps as CheckboxStyleProps,
} from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import React, { FC, useState } from "react";
import { css } from "styled-components";

// Store Custom Code - >
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import { productListItems } from "@store/Themes/common/props/productListItems";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
// <--- Starter Custom Code

export interface ProductListFilterAccordionSectionStyles {
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    seeAllLink?: LinkPresentationProps;
    seeLessLink?: LinkPresentationProps;
    // Store Custom Code
    wrapper?: InjectableCss;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    wrapper: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            ${contentSpacingStyles("0.5em", 0, 0)}
                        `,
                        null,
                        css`
                            ${contentSpacingStyles(0, 0, `${pxToRem(20)}`)}
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    accordionSection: {
        css: css``,
        panelProps: {
            css: css`
                overflow-y: auto;
                overflow-x: hidden;
                max-height: ${pxToRem(350)};
                padding: 0 ${pxToRem(20)} ${pxToRem(26)};
                background-color: ${getColor("common.accentLighter")};
                border: none;

                ::-webkit-scrollbar {
                    width: 0.5em;
                }

                /* Track */
                ::-webkit-scrollbar-track {
                    background: ${getColor("common.border")};
                }

                /* Handle */
                ::-webkit-scrollbar-thumb {
                    background: ${getColor("primary")};
                }
            `,
        },
        headerProps: {
            css: css`
                button {
                    background-color: ${getColor("common.accentLighter")};
                    border: ${pxToRem(2)} solid transparent;
                    padding: ${pxToRem(15)};

                    &:hover * {
                        color: ${getColor("secondary")};
                    }

                    &:focus {
                        padding: ${pxToRem(15)};
                        border-style: solid;
                    }

                    :hover,
                    :active {
                        padding: ${pxToRem(15)};
                    }
                }
            `,
        },
        titleTypographyProps: {
            forwardAs: "h2",
            variant: "h5",
            css: css`
                padding-left: ${pxToRem(5)};
                margin: auto 0;

                ${({ theme }) => `
                    font-size: ${theme.design.typography.size.base};
                    font-weight: ${theme.design.typography.weight.base};
                `}
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;

            &:first-of-type {
                padding-top: ${pxToRem(15)};
                border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            }
        `,
    },
    checkBox: {
        css: css<CheckboxStyleProps>`
            display: flex;
            align-items: flex-start;
        `,
        typographyProps: {
            css: css`
                margin-top: ${pxToRem(-2)};
                word-break: break-all;
            `,
        },
    },
    checkBoxSelected: {
        css: css<CheckboxStyleProps>`
            display: flex;
            align-items: center;
        `,
        typographyProps: {
            css: css`
                word-break: break-all;
            `,
        },
    },
    seeAllLink: productListItems.link,
    seeLessLink: productListItems.link,
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    return (
        <StyledWrapper {...styles.wrapper} /* Store Custom Code */>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
            >
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                            {/* Store Custom Code - > */}
                            {facet.count !== -1 && (
                                <VisuallyHidden>{translate(`item${facet.count > 1 ? "s" : null}`)}</VisuallyHidden>
                            )}
                            {/* <--- Starter Custom Code */}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                            {translate("See Less")}
                        </Link>
                    ) : (
                        <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                            {translate("See All")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default ProductListFiltersAccordionSection;
